import * as L from "leaflet";
import { el, icon } from "./dom";
import { channelLabel, formatRelative, formatSpeed, leadLabel } from "./format";
import type { Household, LiveSession } from "./types";

export const MEDELLIN_CENTER: L.LatLngTuple = [6.2442, -75.5812];
export const MEDELLIN_ZOOM = 13;

const TILE_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const ZONE_BASE_RADIUS_M = 100;
const ZONE_RADIUS_PER_LEAD_M = 5;
const PERMANENT_TOOLTIP_LIMIT = 3;

export type PickListener = (lat: number, lng: number) => void;

function truckTooltip(session: LiveSession): HTMLElement {
  const tip = el("div", { className: "map-tip" });
  const title = el("div", { className: "map-tip__title" });
  title.append(icon("i-truck"), el("span", { text: session.vehicle_label }));
  tip.append(
    title,
    el("div", { className: "map-tip__line", text: `${session.route_name} · ${formatSpeed(session.speed_mps)}` }),
    el("div", { className: "map-tip__line", text: `Última señal ${formatRelative(session.last_seen_at)}` }),
  );
  if (session.device_id.startsWith("gps:")) {
    tip.append(el("div", { className: "map-tip__line", text: "GPS físico del vehículo" }));
  }
  return tip;
}

function householdTooltip(household: Household): HTMLElement {
  const tip = el("div", { className: "map-tip" });
  const title = el("div", { className: "map-tip__title" });
  title.append(icon("i-home"), el("span", { text: household.name }));
  tip.append(
    title,
    el("div", {
      className: "map-tip__line",
      text: `${householdPhone(household)} · aviso ${leadLabel(household.lead_minutes)}`,
    }),
    el("div", { className: "map-tip__line", text: household.channels.map(channelLabel).join(" · ") }),
  );
  return tip;
}

function hasValidCoords(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
}

function householdPhone(household: Household): string {
  const phone = household.phone?.trim() ?? "";
  return phone === "" ? "Sin teléfono" : phone;
}

export class LiveMap {
  private map: L.Map | null = null;
  private truckIcon: L.DivIcon | null = null;
  private pickIcon: L.DivIcon | null = null;
  private permanentTooltips = false;
  private pickMarker: L.Marker | null = null;
  private pickListener: PickListener | null = null;
  private picking = false;

  private readonly truckMarkers = new Map<string, L.Marker>();
  private readonly zones = new Map<string, L.Circle>();
  private readonly points = new Map<string, L.CircleMarker>();

  private readonly handleResize = (): void => {
    this.map?.invalidateSize();
  };

  private readonly handleClick = (event: L.LeafletMouseEvent): void => {
    const listener = this.pickListener;
    if (listener === null) return;
    listener(event.latlng.lat, event.latlng.lng);
  };

  mount(container: HTMLElement): void {
    if (this.map !== null) return;
    const map = L.map(container, {
      center: MEDELLIN_CENTER,
      zoom: MEDELLIN_ZOOM,
      zoomControl: true,
      attributionControl: true,
    });
    L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(map);
    map.on("click", this.handleClick);
    map.whenReady(() => map.invalidateSize());
    this.map = map;
    window.addEventListener("resize", this.handleResize);
  }

  destroy(): void {
    this.clearOverlays();
    window.removeEventListener("resize", this.handleResize);
    this.map?.remove();
    this.map = null;
  }

  get isMounted(): boolean {
    return this.map !== null;
  }

  get isPicking(): boolean {
    return this.picking;
  }

  clearOverlays(): void {
    for (const marker of this.truckMarkers.values()) marker.remove();
    this.truckMarkers.clear();
    for (const zone of this.zones.values()) zone.remove();
    this.zones.clear();
    for (const point of this.points.values()) point.remove();
    this.points.clear();
  }

  updateTrucks(sessions: readonly LiveSession[]): void {
    const map = this.map;
    if (map === null) return;

    const permanent = sessions.length <= PERMANENT_TOOLTIP_LIMIT;
    if (permanent !== this.permanentTooltips) {
      this.permanentTooltips = permanent;
      for (const marker of this.truckMarkers.values()) marker.remove();
      this.truckMarkers.clear();
    }

    const seen = new Set<string>();
    for (const session of sessions) {
      if (!hasValidCoords(session.lat, session.lng)) continue;
      seen.add(session.session_id);
      const position = L.latLng(session.lat, session.lng);
      const tooltip = truckTooltip(session);
      const existing = this.truckMarkers.get(session.session_id);

      if (existing !== undefined) {
        existing.setLatLng(position);
        existing.setTooltipContent(tooltip);
        continue;
      }

      const marker = L.marker(position, {
        icon: this.getTruckIcon(),
        keyboard: false,
        title: `${session.vehicle_label} · ${session.route_name}`,
      })
        .addTo(map)
        .bindTooltip(tooltip, {
          permanent: this.permanentTooltips,
          direction: "top",
          offset: L.point(0, -20),
          opacity: 1,
        });
      this.truckMarkers.set(session.session_id, marker);
    }

    for (const [sessionId, marker] of this.truckMarkers) {
      if (!seen.has(sessionId)) {
        marker.remove();
        this.truckMarkers.delete(sessionId);
      }
    }
  }

  setHouseholds(households: readonly Household[]): void {
    const map = this.map;
    if (map === null) return;
    const seen = new Set<string>();

    for (const household of households) {
      if (!hasValidCoords(household.lat, household.lng)) continue;
      seen.add(household.id);
      const position = L.latLng(household.lat, household.lng);
      const radius = ZONE_BASE_RADIUS_M + household.lead_minutes * ZONE_RADIUS_PER_LEAD_M;
      const tooltip = householdTooltip(household);

      const zone = this.zones.get(household.id);
      if (zone !== undefined) {
        zone.setLatLng(position);
        zone.setRadius(radius);
        zone.setTooltipContent(tooltip);
      } else {
        this.zones.set(
          household.id,
          L.circle(position, {
            radius,
            color: "#8fbfa5",
            weight: 1,
            dashArray: "4 5",
            fillColor: "#2FB673",
            fillOpacity: 0.12,
          })
            .addTo(map)
            .bindTooltip(tooltip, { direction: "top", offset: L.point(0, -10) }),
        );
      }

      const point = this.points.get(household.id);
      if (point !== undefined) {
        point.setLatLng(position);
        point.setTooltipContent(tooltip);
      } else {
        this.points.set(
          household.id,
          L.circleMarker(position, {
            radius: 7,
            color: "#ffffff",
            weight: 2,
            fillColor: "#0F5132",
            fillOpacity: 1,
          })
            .addTo(map)
            .bindTooltip(tooltip, { direction: "top", offset: L.point(0, -10) }),
        );
      }
    }

    for (const [id, zone] of this.zones) {
      if (!seen.has(id)) {
        zone.remove();
        this.zones.delete(id);
      }
    }
    for (const [id, point] of this.points) {
      if (!seen.has(id)) {
        point.remove();
        this.points.delete(id);
      }
    }
  }

  focusHousehold(householdId: string): void {
    const map = this.map;
    const point = this.points.get(householdId);
    if (map === null || point === undefined) return;
    map.panTo(point.getLatLng(), { animate: true });
    point.openTooltip();
  }

  /** Ajusta la vista a camiones y hogares la primera vez que hay datos. */
  fitAll(sessions: readonly LiveSession[], households: readonly Household[]): void {
    const map = this.map;
    if (map === null) return;
    const bounds = L.latLngBounds([]);
    for (const session of sessions) {
      if (hasValidCoords(session.lat, session.lng)) bounds.extend([session.lat, session.lng]);
    }
    for (const household of households) {
      if (hasValidCoords(household.lat, household.lng)) bounds.extend([household.lat, household.lng]);
    }
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [56, 56], maxZoom: 15, animate: false });
  }

  centerOnData(sessions: readonly LiveSession[], households: readonly Household[]): void {
    const map = this.map;
    if (map === null) return;
    const bounds = L.latLngBounds([]);
    for (const session of sessions) {
      if (hasValidCoords(session.lat, session.lng)) bounds.extend([session.lat, session.lng]);
    }
    for (const household of households) {
      if (hasValidCoords(household.lat, household.lng)) bounds.extend([household.lat, household.lng]);
    }
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [56, 56], maxZoom: 15, animate: true });
    else map.setView(MEDELLIN_CENTER, MEDELLIN_ZOOM);
  }

  startPick(listener: PickListener): void {
    const map = this.map;
    if (map === null) return;
    this.pickListener = listener;
    this.picking = true;
    map.getContainer().classList.add("map--picking");
  }

  cancelPick(): void {
    const map = this.map;
    this.pickListener = null;
    this.picking = false;
    map?.getContainer().classList.remove("map--picking");
  }

  setPickedLocation(lat: number | null, lng: number | null): void {
    const map = this.map;
    if (map === null) return;
    if (lat === null || lng === null) {
      this.pickMarker?.remove();
      this.pickMarker = null;
      return;
    }
    const position = L.latLng(lat, lng);
    if (this.pickMarker !== null) {
      this.pickMarker.setLatLng(position);
      return;
    }
    const tip = el("div", { className: "map-tip" });
    const title = el("div", { className: "map-tip__title" });
    title.append(icon("i-pin"), el("span", { text: "Nueva ubicación" }));
    tip.append(title, el("div", { className: "map-tip__line", text: `${lat.toFixed(5)}, ${lng.toFixed(5)}` }));

    this.pickMarker = L.marker(position, {
      icon: this.getPickIcon(),
      keyboard: false,
      zIndexOffset: 800,
    })
      .addTo(map)
      .bindTooltip(tip, { permanent: true, direction: "top", offset: L.point(0, -34), opacity: 1 });
  }

  panTo(lat: number, lng: number): void {
    this.map?.panTo([lat, lng], { animate: true });
  }

  private getTruckIcon(): L.DivIcon {
    if (this.truckIcon === null) {
      const inner = el("div", { className: "truck-marker__inner" });
      inner.append(el("span", { className: "truck-marker__ping" }), icon("i-truck"));
      this.truckIcon = L.divIcon({
        className: "truck-marker",
        html: inner,
        iconSize: [34, 34],
        iconAnchor: [17, 17],
      });
    }
    return this.truckIcon;
  }

  private getPickIcon(): L.DivIcon {
    if (this.pickIcon === null) {
      const inner = el("div", { className: "pick-marker__inner" });
      inner.append(icon("i-pin"));
      this.pickIcon = L.divIcon({
        className: "pick-marker",
        html: inner,
        iconSize: [34, 44],
        iconAnchor: [17, 42],
      });
    }
    return this.pickIcon;
  }
}
